import { apiClient } from './api';
import { Employee } from '../types/employee';
import { UserRole } from '../contexts/AuthContext';

export interface CreateEmployeeDto {
  empNo?: string;
  name: string;
  position: string;
  rank: string;
  department: string;
  tel: string;
  email: string;
  age?: number;
  joinDate: string;
  endDate?: string;
  annualSalary?: number;
  status?: string;
  ssn?: string;
  bankAccount?: string;
  education?: Array<{
    degree: string;
    major: string;
    school: string;
  }>;
  experience?: Array<{
    company: string;
    department: string;
    position: string;
  }>;
}

export interface UpdateEmployeeDto extends Partial<CreateEmployeeDto> {}

export const employeeApi = {
  getAll: (currentUserRole?: UserRole | null): Promise<Employee[]> => {
    return apiClient.get<Employee[]>('/employees');
  },

  // 필터링된 직원 목록 조회 (백엔드에서 DEVELOPER, VIRTUAL 제외)
  getAllVisible: (currentUserRole?: UserRole | null, department?: string, status?: string): Promise<Employee[]> => {
    const params = new URLSearchParams();
    if (currentUserRole) {
      params.append('userRole', currentUserRole);
    }
    if (department) {
      params.append('department', department);
    }
    if (status) {
      params.append('status', status);
    }
    const queryString = params.toString();
    return apiClient.get<Employee[]>(`/employees/visible${queryString ? `?${queryString}` : ''}`);
  },

  getActive: (currentUserRole?: UserRole | null): Promise<Employee[]> => {
    return apiClient.get<Employee[]>('/employees/active');
  },

  create: (employee: CreateEmployeeDto): Promise<Employee> => {
    return apiClient.post<Employee>('/employees', employee);
  },

  update: (id: number, employee: UpdateEmployeeDto): Promise<Employee> => {
    return apiClient.patch<Employee>(`/employees/${id}`, employee);
  },

  delete: (id: number): Promise<void> => {
    return apiClient.delete<void>(`/employees/${id}`);
  },

  restore: (id: number): Promise<Employee> => {
    return apiClient.post<Employee>(`/employees/${id}/restore`, {});
  },

  getDeleted: (): Promise<Employee[]> => {
    return apiClient.get<Employee[]>('/employees/deleted/list');
  },

  // HR Cost 관련 API
  getHRCostByYear: (id: number, year: number): Promise<any> => {
    return apiClient.get<any>(`/employees/${id}/hr-cost/${year}`);
  },

  updateHRCost: (id: number, year: number, data: any): Promise<any> => {
    return apiClient.patch<any>(`/employees/${id}/hr-cost/${year}`, data);
  },

  // 모든 직원의 HR Cost 조회 (연도별)
  getAllHRCostByYear: (year: number): Promise<Array<{
    employee: {
      id: number;
      name: string;
      position: string;
      department: string;
      status?: string;
      annualSalary: string;
      joinDate?: string;
    };
    hrCost: any;
  }>> => {
    return apiClient.get<Array<{
      employee: {
        id: number;
        name: string;
        position: string;
        department: string;
        status?: string;
        annualSalary: string;
        joinDate?: string;
      };
      hrCost: any;
    }>>(`/employees/hr-cost-all/${year}`);
  },

  // 휴직/퇴사 관련 API
  processLeaveRequest: (id: number, data: {
    startDate: string;
    returnDate: string;
    reason: string;
    payType: 'paid' | 'unpaid';
    memo?: string;
  }): Promise<Employee> => {
    return apiClient.post<Employee>(`/employees/${id}/leave-request`, data);
  },

  processResignationRequest: (id: number, data: {
    resignDate: string;
    reason: string;
    leaveAccrued: number;
    leaveUsed: number;
    leaveRemaining: number;
    leaveAllowance: number;
    severancePay: 'yes' | 'no';
    memo?: string;
  }): Promise<Employee> => {
    return apiClient.post<Employee>(`/employees/${id}/resignation-request`, data);
  },

  processReturnFromLeave: (id: number, memo?: string): Promise<Employee> => {
    return apiClient.post<Employee>(`/employees/${id}/return-from-leave`, { memo });
  },

  cancelResignation: (id: number): Promise<Employee> => {
    return apiClient.post<Employee>(`/employees/${id}/cancel-resignation`, {});
  },

  // Year 관련 API
  getAllYears: (): Promise<Array<{ year: number }>> => {
    return apiClient.get<Array<{ year: number }>>('/employees/years');
  },

  // HR Cost Settings 관련 API
  getHRCostSettings: (year: number): Promise<{
    id: number;
    year: number;
    epsRate: number;
    ecmRate: number;
    createdAt: Date;
    updatedAt: Date;
  }> => {
    return apiClient.get<{
      id: number;
      year: number;
      epsRate: number;
      ecmRate: number;
      createdAt: Date;
      updatedAt: Date;
    }>(`/employees/hr-cost-settings/${year}`);
  },

  updateHRCostSettings: (year: number, data: {
    epsRate?: number;
    ecmRate?: number;
  }): Promise<{
    id: number;
    year: number;
    epsRate: number;
    ecmRate: number;
    createdAt: Date;
    updatedAt: Date;
  }> => {
    return apiClient.put<{
      id: number;
      year: number;
      epsRate: number;
      ecmRate: number;
      createdAt: Date;
      updatedAt: Date;
    }>(`/employees/hr-cost-settings/${year}`, data);
  },

  // 휴직/복직/퇴사/퇴사취소 히스토리 관련 API (전체)
  getAllLongTermLeaveHistory: (): Promise<Array<{
    id: number;
    type: 'LEAVE' | 'RETURN' | 'RESIGNATION' | 'CANCEL_RESIGNATION';
    effectiveDate: string;
    expectedReturnDate?: string;
    reason?: string;
    payType?: 'paid' | 'unpaid';
    memo?: string;
    previousStatus: string;
    newStatus: string;
    createdAt: string;
    employee: {
      id: number;
      name: string;
      empNo: string;
      department: string;
      position: string;
    };
  }>> => {
    return apiClient.get<Array<{
      id: number;
      type: 'LEAVE' | 'RETURN' | 'RESIGNATION' | 'CANCEL_RESIGNATION';
      effectiveDate: string;
      expectedReturnDate?: string;
      reason?: string;
      payType?: 'paid' | 'unpaid';
      memo?: string;
      previousStatus: string;
      newStatus: string;
      createdAt: string;
      employee: {
        id: number;
        name: string;
        empNo: string;
        department: string;
        position: string;
      };
    }>>('/employees/leave-history/all');
  },
};
